<template>
  <div class="soil-triangle-container">
    <svg id="plot" width="450" height="400"></svg>
    <div class="soil-triangle-legend" aria-hidden="true">
      <svg width="12" height="12" class="legend-icon" focusable="false">
        <circle class="point-rim" cx="6" cy="6" r="5"></circle>
        <circle class="point" cx="6" cy="6" r="4"></circle>
      </svg>
      <span class="legend-text">each point represents the average soil texture of a field in this farm</span>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { getThemeVariable, hexToRgb } from "@/utility";

export default {
  name: 'SoilTextureTriangle',
  props: {
    samplePoints: {
      type: Array,
      default: () => [
        { sand: 40, silt: 40, clay: 20, label: "" },
        { sand: 10, silt: 60, clay: 30, label: "" },
        { sand: 85, silt: 10, clay: 5, label: "" },
        { sand: 20, silt: 22, clay: 70, label: "" },
      ]
    }
  },
  data() {
    // Helper function to blend colors
    const blendColors = (color1, color2, ratio = 0.5) => {
      const rgb1 = hexToRgb(color1);
      const rgb2 = hexToRgb(color2);

      if (!rgb1 || !rgb2) return "#CCCCCC";

      const r = Math.round(rgb1.r * (1 - ratio) + rgb2.r * ratio);
      const g = Math.round(rgb1.g * (1 - ratio) + rgb2.g * ratio);
      const b = Math.round(rgb1.b * (1 - ratio) + rgb2.b * ratio);

      return `rgb(${r}, ${g}, ${b})`;
    };

    // Helper function to blend three colors
    const blendThreeColors = (color1, color2, color3, ratio1 = 0.33, ratio2 = 0.33) => {
      const rgb1 = hexToRgb(color1);
      const rgb2 = hexToRgb(color2);
      const rgb3 = hexToRgb(color3);

      if (!rgb1 || !rgb2 || !rgb3) return "#CCCCCC";

      const ratio3 = 1 - ratio1 - ratio2;

      const r = Math.round(rgb1.r * ratio1 + rgb2.r * ratio2 + rgb3.r * ratio3);
      const g = Math.round(rgb1.g * ratio1 + rgb2.g * ratio2 + rgb3.g * ratio3);
      const b = Math.round(rgb1.b * ratio1 + rgb2.b * ratio2 + rgb3.b * ratio3);

      return `rgb(${r}, ${g}, ${b})`;
    };

    // Get base theme colors
    const peach = getThemeVariable("--arva-peach");
    const cream = getThemeVariable("--arva-cream");
    const fluoGreen = getThemeVariable("--arva-fluo-green");
    const darkGreen = getThemeVariable("--arva-dark-green");
    const lightGreen = getThemeVariable("--arva-light-green");
    const blueSky = getThemeVariable("--arva-blue-sky");
    const brown = getThemeVariable("--arva-brown");

    return {
      // USDA soil classification regions
      soilRegions: [
        {
          name: "Sand",
          points: [[100, 0, 0], [90, 0, 10], [85, 15, 0]],
          color: peach // Sand - peach
        },
        {
          name: "Loamy Sand",
          points: [[90, 0, 10], [85, 0, 15], [70, 30, 0], [85, 15, 0]],
          color: blendColors(peach, cream, 0.3) // Loamy Sand - peach toward cream
        },
        {
          name: "Sandy Loam",
          points: [[85, 0, 15], [80, 0, 20], [52, 28, 20], [52, 41, 7], [43, 50, 7], [50, 50, 0], [70, 30, 0]],
          color: blendColors(peach, lightGreen, 0.4) // Sandy Loam - peach + light green
        },
        {
          name: "Loam",
          points: [[52, 41, 7], [52, 28, 20], [45, 28, 27], [23, 50, 27], [43, 50, 7]],
          color: fluoGreen // Loam - fluo green (standout)
        },
        {
          name: "Silt Loam",
          points: [[50, 50, 0], [23, 50, 27], [0, 73, 27], [0, 88, 12], [8, 80, 12], [20, 80, 0]],
          color: blendColors(cream, lightGreen, 0.3) // Silt Loam - cream with light green
        },
        {
          name: "Silt",
          points: [[20, 80, 0], [8, 80, 12], [0, 88, 12], [0, 100, 0]],
          color: cream // Silt - cream
        },
        {
          name: "Sandy Clay Loam",
          points: [[80, 0, 20], [65, 0, 35], [45, 20, 35], [45, 28, 27], [52, 28, 20]],
          color: blendThreeColors(peach, brown, fluoGreen, 0.4, 0.4) // Sandy Clay Loam - mix of sand, clay, and loam
        },
        {
          name: "Clay Loam",
          points: [[45, 28, 27], [45, 15, 40], [20, 40, 40], [20, 53, 27], [23, 50, 27]],
          color: blendColors(darkGreen, fluoGreen, 0.4) // Clay Loam - transition from clay to loam
        },
        {
          name: "Silty Clay Loam",
          points: [[20, 53, 27], [20, 40, 40], [0, 60, 40], [0, 73, 27]],
          color: blendThreeColors(cream, darkGreen, fluoGreen, 0.3, 0.4) // Silty Clay Loam - silt + clay + loam
        },
        {
          name: "Sandy Clay",
          points: [[65, 0, 35], [45, 0, 55], [45, 20, 35]],
          color: brown // Sandy Clay - brown
        },
        {
          name: "Silty Clay",
          points: [[20, 40, 40], [0, 40, 60], [0, 60, 40]],
          color: blendColors(blueSky, darkGreen, 0.6) // Silty Clay - blend of blue-sky and dark green
        },
        {
          name: "Clay",
          points: [[45, 15, 40], [45, 0, 55], [0, 0, 100], [0, 40, 60], [20, 40, 40]],
          color: darkGreen // Clay - dark green
        }
      ]
    }
  },
  mounted() {
    this.drawTriangle();
  },
  watch: {
    samplePoints: {
      handler() {
        this.drawTriangle();
      },
      deep: true
    }
  },
  methods: {
    drawTriangle() {
      // Clear existing SVG content
      d3.select(this.$el).select("svg").selectAll("*").remove();

      // Setup
      const svg = d3.select(this.$el).select("svg");
      const width = 450;
      const height = 450;
      const size = Math.min(width, height - 50);
      const radius = size * 0.5;
      const h = radius * Math.sqrt(3);
      const center = { x: width / 2, y: height / 2 + 20 };

      // Create main group
      const g = svg.append("g");

      // Triangle vertices
      const vertices = {
        sand: { x: center.x - h / 2, y: center.y + radius / 2 }, // bottom left
        silt: { x: center.x + h / 2, y: center.y + radius / 2 }, // bottom right
        clay: { x: center.x, y: center.y - radius } // top
      };

      // Coordinate transformation functions
      const ternaryToCartesian = (sand, silt, clay) => {
        // Normalize
        const sum = sand + silt + clay;
        const s = sand / sum;
        const si = silt / sum;
        const c = clay / sum;

        // Calculate cartesian coordinates
        const x = vertices.sand.x * s + vertices.silt.x * si + vertices.clay.x * c;
        const y = vertices.sand.y * s + vertices.silt.y * si + vertices.clay.y * c;

        return { x, y };
      };

      // Draw grid
      const gridG = g.append("g").attr("class", "grid");

      for (let i = 10; i <= 90; i += 10) {
        // Sand grid lines (parallel to silt-clay edge)
        const p1 = ternaryToCartesian(i, 100 - i, 0);
        const p2 = ternaryToCartesian(i, 0, 100 - i);
        gridG.append("line")
          .attr("x1", p1.x).attr("y1", p1.y)
          .attr("x2", p2.x).attr("y2", p2.y);

        // Silt grid lines (parallel to sand-clay edge)
        const p3 = ternaryToCartesian(100 - i, i, 0);
        const p4 = ternaryToCartesian(0, i, 100 - i);
        gridG.append("line")
          .attr("x1", p3.x).attr("y1", p3.y)
          .attr("x2", p4.x).attr("y2", p4.y);

        // Clay grid lines (parallel to sand-silt edge)
        const p5 = ternaryToCartesian(100 - i, 0, i);
        const p6 = ternaryToCartesian(0, 100 - i, i);
        gridG.append("line")
          .attr("x1", p5.x).attr("y1", p5.y)
          .attr("x2", p6.x).attr("y2", p6.y);
      }

      // Draw triangle outline
      g.append("polygon")
        .attr("points", `${vertices.sand.x},${vertices.sand.y} ${vertices.silt.x},${vertices.silt.y} ${vertices.clay.x},${vertices.clay.y}`)
        .attr("fill", "none")
        .attr("stroke", "#333")
        .attr("stroke-width", 2);

      // Draw soil regions
      const regionsG = g.append("g");

      this.soilRegions.forEach(region => {
        const points = region.points.map(p => {
          const pt = ternaryToCartesian(p[0], p[1], p[2]);
          return `${pt.x},${pt.y}`;
        }).join(" ");

        regionsG.append("polygon")
          .attr("class", "soil-region")
          .attr("points", points)
          .attr("fill", region.color);

        // Calculate centroid for label
        const centroid = region.points.reduce((acc, p) => {
          return [acc[0] + p[0], acc[1] + p[1], acc[2] + p[2]];
        }, [0, 0, 0]).map(v => v / region.points.length);

        const labelPos = ternaryToCartesian(centroid[0], centroid[1], centroid[2]);

        // Split text for multi-word labels
        const words = region.name.split(' ');
        if (words.length > 1) {
          regionsG.append("text")
            .attr("class", "region-label")
            .attr("x", labelPos.x)
            .attr("y", labelPos.y - 5)
            .attr("text-anchor", "middle")
            .text(words[0]);

          for (let i = 1; i < words.length; i++) {
            regionsG.append("text")
              .attr("class", "region-label")
              .attr("x", labelPos.x)
              .attr("y", labelPos.y - 5 + (i * 10))
              .attr("text-anchor", "middle")
              .text(words[i]);
          }
        } else {
          regionsG.append("text")
            .attr("class", "region-label")
            .attr("x", labelPos.x)
            .attr("y", labelPos.y)
            .attr("text-anchor", "middle")
            .attr("dy", "0.3em")
            .text(region.name);
        }
      });

      // Add axis labels
      g.append("text")
        .attr("class", "axis-title")
        .attr("x", (vertices.sand.x + vertices.clay.x) / 2 - 50)
        .attr("y", (vertices.sand.y + vertices.clay.y) / 2)
        .attr("text-anchor", "middle")
        .attr("transform", `rotate(-60 ${(vertices.sand.x + vertices.clay.x) / 2 - 50} ${(vertices.sand.y + vertices.clay.y) / 2})`)
        .text("Clay (%)");

      g.append("text")
        .attr("class", "axis-title")
        .attr("x", (vertices.silt.x + vertices.clay.x) / 2 + 50)
        .attr("y", (vertices.silt.y + vertices.clay.y) / 2)
        .attr("text-anchor", "middle")
        .attr("transform", `rotate(60 ${(vertices.silt.x + vertices.clay.x) / 2 + 50} ${(vertices.silt.y + vertices.clay.y) / 2})`)
        .text("Silt (%)");

      g.append("text")
        .attr("class", "axis-title")
        .attr("x", center.x)
        .attr("y", vertices.sand.y + 40)
        .attr("text-anchor", "middle")
        .text("Sand (%)");

      // Add percentage labels along edges
      // Clay percentages (left edge)
      for (let i = 10; i <= 90; i += 10) {
        const pos = ternaryToCartesian(100 - i, 0, i);
        g.append("text")
          .attr("class", "tick")
          .attr("x", pos.x - 15)
          .attr("y", pos.y + 3)
          .attr("text-anchor", "end")
          .text(i);
      }

      // Silt percentages (right edge)
      for (let i = 10; i <= 90; i += 10) {
        const pos = ternaryToCartesian(0, i, 100 - i);
        g.append("text")
          .attr("class", "tick")
          .attr("x", pos.x + 15)
          .attr("y", pos.y + 3)
          .attr("text-anchor", "start")
          .text(i);
      }

      // Sand percentages (bottom edge)
      for (let i = 10; i <= 90; i += 10) {
        const pos = ternaryToCartesian(i, 100 - i, 0);
        g.append("text")
          .attr("class", "tick")
          .attr("x", pos.x)
          .attr("y", pos.y + 15)
          .attr("text-anchor", "middle")
          .text(i);
      }

      // Add vertex labels
      g.append("text")
        .attr("class", "tick")
        .attr("x", vertices.clay.x)
        .attr("y", vertices.clay.y - 10)
        .attr("text-anchor", "middle")
        .text("100");

      g.append("text")
        .attr("class", "tick")
        .attr("x", vertices.sand.x - 20)
        .attr("y", vertices.sand.y + 5)
        .attr("text-anchor", "middle")
        .text("100");

      g.append("text")
        .attr("class", "tick")
        .attr("x", vertices.silt.x + 20)
        .attr("y", vertices.silt.y + 5)
        .attr("text-anchor", "middle")
        .text("100");

      // Plot soil samples
      const pointsG = g.append("g");

      this.samplePoints.forEach((sample) => {
        if (sample.sand == null || sample.silt == null || sample.clay == null) {
          return; // skip invalid points
        }
        const pos = ternaryToCartesian(sample.sand, sample.silt, sample.clay);

        // Add a subtle rim/glow effect
        pointsG.append("circle")
          .attr("class", "point-rim")
          .attr("cx", pos.x)
          .attr("cy", pos.y)
          .attr("r", 4);

        // Main point circle (smaller and more subtle)
        pointsG.append("circle")
          .attr("class", "point")
          .attr("cx", pos.x)
          .attr("cy", pos.y)
          .attr("r", 3);

        pointsG.append("text")
          .attr("class", "point-label")
          .attr("x", pos.x)
          .attr("y", pos.y - 8)
          .attr("text-anchor", "middle")
          .text(sample.label);
      });
    }
  }
}
</script>

<style>
.soil-triangle-container {
  font-family: 'Inter', sans-serif !important;

}

.soil-triangle-container .grid line {
  stroke: #ccc;
  stroke-width: 0.3;
  stroke-dasharray: 2,2;
}

.soil-triangle-container .soil-region {
  fill-opacity: 0.7;
  stroke: #333;
  stroke-width: 0.8;
}

.soil-triangle-container .point-rim {
  fill: rgba(255, 255, 255, 0.7);
  stroke: rgba(0, 0, 0, 0.2);
  stroke-width: 0.5px;
}

.soil-triangle-container .point {
  fill: red;
  stroke: white;
  stroke-width: 1.1px;
  opacity: 0.6;
}

.soil-triangle-container .tick {
  font-size: 12px;
  fill: var(--arva-black)
}

.soil-triangle-container .axis-title {
  font-size: 12px;
  font-weight: bold;
  fill: var(--arva-black)
}

.soil-triangle-container .region-label {
  font-size: 10px;
  pointer-events: none;
  fill: var(--arva-black)
}

.soil-triangle-container .point-label {
  font-size: 10px;
  fill: var(--arva-black);
}

.soil-triangle-legend {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 0;
  justify-content: center;
}

.soil-triangle-legend .legend-text {
  font-size: 10px;
  color: var(--arva-black);
}
</style>
